import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirt } from '@phosphor-icons/react'

import { BackListButton, Loading } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import ProductType from '@/metrics/components/ProductType/ProductType'
import { groupByValues } from '@/metrics/constants'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import CategoryMetrics from './CategoryMetrics/CategoryMetrics'
import * as Styles from './styles'

const CategoryMetricDetailPage = ({ isUnifiedDashboard }) => {
  const { categoryPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated

  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { data: categories, loading: loadingCategory } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: {
      groupBy: groupByValues.categories,
      ordering: productsFilters?.ordering,
      query: productsFilters?.query,
    },
    doRequest: !!categoryPosition,
  })

  const category = categories?.results?.[categoryPosition]

  const getBackUrl = () => {
    return `${isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage}?${getUrlSearchParams()}`
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getBackUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={TShirt}
        title={getTitleByOrdering('Categorías', productsFilters?.ordering, t)}
        loading={loadingCategory}
        item={
          category && (
            <ProductType productType={{ ...category, index: Number(categoryPosition) + 1 }} showAmounts={false} />
          )
        }
        count={category?.count}
        percentage={category?.percentage}
        sales={category?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />
      {category && (
        <Loading loading={loadingCategory}>
          <CategoryMetrics
            categoryId={category.id}
            returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
            commonFilters={commonFilters}
            returnSalesRatioFilters={returnSalesRatioFilters}
            setReturnSalesRatioFilters={setReturnSalesRatioFilters}
            returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
            setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
            categoryPosition={categoryPosition}
            salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
            productsOrdering={productsFilters?.ordering}
            isUnifiedDashboard={isUnifiedDashboard}
          />
        </Loading>
      )}
    </Styles.Container>
  )
}

export default CategoryMetricDetailPage
